// ═══════════════════════════════════════════════════════════════════════════
// RUN 17: Remove Excess Empty Paragraphs  [v5 — reduced cognitive complexity]
// ═══════════════════════════════════════════════════════════════════════════

import { reportResult } from '../runs/common';

async function hasPageBreakOrDrawing(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<boolean> {
  try {
    const ooxml = para.getOoxml();
    await context.sync();
    const xml = ooxml.value || "";
    return (
      xml.includes('w:type="page"') ||
      xml.includes('w:type="column"') ||
      xml.includes("wp:anchor") ||
      xml.includes("o:OLEObject") ||
      xml.includes("v:shape") ||
      xml.includes("v:imagedata") ||
      xml.includes("m:oMath")
    );
  } catch {
    return false;
  }
}

async function isAlreadyTrackedDeleted(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<boolean> {
  try {
    const ooxml = para.getOoxml();
    await context.sync();
    const xml = ooxml.value || "";
    return xml.includes("<w:del ") && xml.includes('w:author=');
  } catch {
    return false;
  }
}

/** Reasons a paragraph is protected from deletion. */
const enum SkipReason {
  TableCell = "table-cell",
  LastParagraph = "last-paragraph",
  TrackedDeleted = "tracked-deleted",
  HasText = "has-text",
  InlinePicture = "inline-picture",
  ContentControl = "content-control",
  PageBreak = "page-break",
  None = "none",
}

/**
 * Fast sync checks — no XML fetch required.
 * Returns a SkipReason if the paragraph is already disqualified, or null
 * to signal that the slower async checks should run next.
 */
function getSyncSkipReason(para: Word.Paragraph): SkipReason | null {
  if (!para.parentTableCellOrNullObject.isNullObject) return SkipReason.TableCell;
  if (para.isLastParagraph)                           return SkipReason.LastParagraph;
  if ((para.text || "").trim().length > 0)            return SkipReason.HasText;
  if (para.inlinePictures.items.length > 0)           return SkipReason.InlinePicture;
  if (para.contentControls.items.length > 0)          return SkipReason.ContentControl;
  return null;
}

/**
 * Slow async checks — fetches OOXML only when sync checks pass.
 * Returns a SkipReason if the paragraph must be protected, or None if it
 * is truly empty and eligible for deletion.
 */
async function getAsyncSkipReason(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<SkipReason> {
  if (await isAlreadyTrackedDeleted(para, context)) return SkipReason.TrackedDeleted;
  if (await hasPageBreakOrDrawing(para, context))   return SkipReason.PageBreak;
  return SkipReason.None;
}

/**
 * Combines both checks. Returns the reason a paragraph should be skipped,
 * or SkipReason.None when it is a candidate for deletion.
 */
async function getSkipReason(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<SkipReason> {
  return getSyncSkipReason(para) ?? getAsyncSkipReason(para, context);
}

/**
 * PHASE 1 — scans loaded paragraphs and returns those marked for deletion.
 * Pure detection; no mutations.
 */
async function detectExcessEmpties(
  paragraphs: Word.ParagraphCollection,
  context: Word.RequestContext,
  sectionIndex: number
): Promise<Word.Paragraph[]> {
  const toDelete: Word.Paragraph[] = [];
  let consecutiveEmpty = 0;

  for (let i = 0; i < paragraphs.items.length; i++) {
    const para = paragraphs.items[i];
    const tag  = `[S${sectionIndex + 1} P${i + 1}]`;
    const reason = await getSkipReason(para, context);

    if (reason === SkipReason.LastParagraph) {
      console.log(`${tag} Last paragraph — protected`);
      break;
    }
    if (reason === SkipReason.TableCell) { consecutiveEmpty = 0; continue; }
    if (reason === SkipReason.TrackedDeleted) {
      console.log(`${tag} Already tracked-deleted — skipping (not counted)`);
      continue; // intentionally no reset
    }
    if (reason !== SkipReason.None) {
      console.log(`${tag} Protected (${reason})`);
      consecutiveEmpty = 0;
      continue;
    }

    consecutiveEmpty++;
    if (consecutiveEmpty > 1) {
      toDelete.push(para);
      console.log(`${tag} Marked for deletion (consecutive empty #${consecutiveEmpty})`);
    } else {
      console.log(`${tag} Empty — keeping (first in run)`);
    }
  }

  return toDelete;
}

/**
 * PHASE 2 — deletes the paragraphs collected by detectExcessEmpties.
 * Runs inside the same Word.run context so proxies remain valid.
 */
async function deleteMarkedParas(
  toDelete: Word.Paragraph[],
  context: Word.RequestContext,
  sectionIndex: number
): Promise<number> {
  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
  await context.sync();

  let deleted = 0;

  for (const para of toDelete) {
    try {
      para.delete();
      await context.sync();
      deleted++;
      console.log(`[S${sectionIndex + 1}] ✓ Deleted`);
    } catch (e) {
      console.log(`[S${sectionIndex + 1}] Skipped — Word rejected deletion: ${e.message}`);
      try {
        context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
        await context.sync();
      } catch { /* context rebuild failed */ }
    }
  }

  context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
  await context.sync();

  return deleted;
}

async function processSectionParas(sectionIndex: number): Promise<number> {
  return await Word.run(async (context) => {
    const sections = context.document.sections;
    sections.load("items");
    await context.sync();

    if (sectionIndex >= sections.items.length) return 0;

    const paragraphs = sections.items[sectionIndex].body.paragraphs;
    paragraphs.load("items");
    await context.sync();

    console.log(`\n[Section ${sectionIndex + 1}] paragraphs: ${paragraphs.items.length}`);

    for (const para of paragraphs.items) {
      para.load("text, isLastParagraph");
      para.parentTableCellOrNullObject.load("isNullObject");
      para.inlinePictures.load("items");
      para.contentControls.load("items");
    }
    await context.sync();

    const toDelete = await detectExcessEmpties(paragraphs, context, sectionIndex);
    console.log(`[Section ${sectionIndex + 1}] Marked: ${toDelete.length}`);

    if (toDelete.length === 0) return 0;

    const deleted = await deleteMarkedParas(toDelete, context, sectionIndex);
    console.log(`[Section ${sectionIndex + 1}] Deleted: ${deleted} / Marked: ${toDelete.length}`);
    return deleted;
  });
}

export async function run17(): Promise<void> {
  try {
    console.log("=".repeat(70));
    console.log("=== REMOVE EXCESS EMPTY PARAGRAPHS ===");
    console.log("=".repeat(70));

    const sectionCount = await Word.run(async (context) => {
      const sections = context.document.sections;
      sections.load("items");
      await context.sync();
      return sections.items.length;
    });

    console.log(`Total sections: ${sectionCount}`);

    let totalDeleted = 0;

    for (let s = 0; s < sectionCount; s++) {
      const deleted = await processSectionParas(s);
      totalDeleted += deleted;
    }

    console.log("\n" + "=".repeat(70));
    console.log(`✓ Total deleted: ${totalDeleted} excess empty paragraph(s).`);
    console.log("=".repeat(70));

    if (totalDeleted > 0) {
      reportResult('Paragraph Formatting Complete', [
        { label: 'Excess empty paragraphs removed', count: totalDeleted },
      ]);
    } else {
      reportResult('Paragraph Formatting — no changes needed', [
        { label: 'No excess empty paragraphs found', note: 'No changes made' },
      ]);
    }
  } catch (error) {
    console.error("Error in run17:", error);
    console.error("Message:", error.message);
  }
}